import React, { useMemo } from "react";
import cx from "classnames";
import { CloseIcon, Form, Input, Button, TextArea, toast } from "@picklebet/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useUserToken } from "@/features/auth/hooks/useUserToken";
import { useIsLoggedIn } from "@/features/auth/hooks/useIsLoggedIn";
import { useProfile } from "@/hooks/firestore/user/useProfile";
import { useZendeskDomain } from "@/hooks/firestore/user/useZendesk";
import { getBridge } from "utilities/bridge";
import { createATicket } from "utilities/api/picklebet/zendesk";
import { isWebView } from "utilities/display";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { convertErrorToToast } from "utilities/errorsParser";
import { attempt } from "utilities/attempt";

import * as styles from "./HelpCenter.module.scss";

type CreateATicketProps = {
  visible: boolean;
  setVisible: (visible: boolean) => void;
};

const makeSchema = (loggedIn: boolean) => {
  return z.object({
    description: z.string().min(1, { message: "Description is required." }),
    subject: z.string().min(1, { message: "Subject is required." }),
    email: loggedIn
      ? z.string().optional()
      : z
          .string({ message: "Email is required." })
          .email("Email must be valid"),
  });
};
export const CreateATicket = ({ visible, setVisible }: CreateATicketProps) => {
  const token = useUserToken();
  const loggedIn = useIsLoggedIn();
  const { profile } = useProfile();
  const zendeskDomain = useZendeskDomain();

  const openLinkExternallyOfTheApps = (href: string) => {
    if (isWebView()) {
      getBridge().send.openExternally({ url: href });
    } else {
      window.open(href, "_blank");
    }
  };

  const schema = useMemo(() => {
    return makeSchema(loggedIn);
  }, [loggedIn]);

  const {
    handleSubmit,
    register,
    formState: { isSubmitting, errors },
    watch,
  } = useForm({
    resolver: zodResolver(schema),
  });

  return (
    <Form
      onSubmit={handleSubmit(async ({ email, subject, description }) => {
        if (!token) return;

        const fetchResult = await attempt(
          createATicket({
            token,
            payload: { email: email || "", subject, description },
          }),
        );

        if (!fetchResult.ok) {
          toast(
            convertErrorToToast(fetchResult.error, {
              title: "Something went wrong",
            }),
          );
          return;
        }

        toast({
          description: "Your request has been submitted.",
          variant: "success",
        });
        setVisible(false);
      })}
    >
      <div className={cx(styles.helpCenter, { [styles.visible]: visible })}>
        {isSubmitting && <span className={styles.loader}></span>}
        <div className={styles.header}>
          <div className={styles.title} onClick={() => setVisible(false)}>
            <div className={styles.titleText}>Picklebet Help Center</div>
            <div className={styles.icon}>
              <CloseIcon />
            </div>
          </div>
        </div>
        <div className={styles.formContent}>
          <div>
            <Input
              label="Subject"
              {...register("subject")}
              errorMessage={errors?.subject?.message as string}
            />
            {!loggedIn && (
              <Input
                label="Email"
                {...register("email")}
                errorMessage={errors?.email?.message as string}
              />
            )}
            <TextArea
              label="Description"
              {...register("description")}
              errorMessage={errors?.description?.message as string}
            />

            <div className={styles.uploadDocuments}>
              <div>
                Need to{" "}
                <span
                  className={styles.greenLink}
                  onClick={() =>
                    openLinkExternallyOfTheApps(
                      `https://${zendeskDomain}/hc/en-us/requests/new?subject=${watch(
                        "subject",
                      )}&description=${watch(
                        "description",
                      )}&anonymous_requester_email=${
                        profile?.email ?? watch("email")
                      }`,
                    )
                  }
                >
                  {"  "}Upload Documents?
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className={cx(styles.footer, styles.formFooter)}>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={isSubmitting}
          >
            Create a ticket
          </Button>
        </div>
      </div>
    </Form>
  );
};
